import { memo, ReactNode, useCallback, useState, useEffect, useRef } from 'react'
import tw from 'twin.macro'
import { Button } from './Button'
import { BehaviorMajor } from '@shopify/polaris-icons'
import { FileModal } from './FileModal'
import { FileDownloadButton } from './FileDownloadButton'

interface Props {
  className?: any
  previewContent: ReactNode
  content?: ReactNode
  fullScreen?: boolean
  onOpen?: () => void | Promise<void>
  filePath?: string
  fileName?: string
  partnerDriverId?: string
  stepId?: string
  documentId?: string
  fileExtension?: string
  header?: React.ReactNode
}

const FileWithPreviewComponent = ({
  className,
  content,
  previewContent,
  fullScreen,
  onOpen,
  filePath,
  fileName,
  partnerDriverId,
  stepId,
  documentId,
  fileExtension,
  header,
}: Props) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const pendingOpenRef = useRef(false)

  useEffect(() => {
    if (pendingOpenRef.current && filePath) {
      setIsPreviewOpen(true)
      pendingOpenRef.current = false
    }
  }, [filePath])

  const openPreview = useCallback(async () => {
    if (onOpen) {
      await onOpen()

      if (!filePath) {
        pendingOpenRef.current = true
        setTimeout(() => {
          if (pendingOpenRef.current) {
            setIsPreviewOpen(true)
            pendingOpenRef.current = false
          }
        }, 100)
      } else {
        setIsPreviewOpen(true)
      }
    } else {
      setIsPreviewOpen(true)
    }
  }, [onOpen, filePath])

  const closePreview = useCallback(() => {
    setIsPreviewOpen(false)
  }, [])

  return (
    <div css={[tw`relative inline-block overflow-hidden w-full`, className]}>
      {previewContent}
      <div css={tw`absolute top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.3)]`}>
        <div css={tw`[transform: translate(-50%, -50%)] top-1/2 left-1/2 absolute flex gap-2`}>
          <Button
            size="small"
            color="grey"
            rightIcon={<BehaviorMajor width={20} />}
            onClick={openPreview}
          >
            Open file
          </Button>
          <FileDownloadButton
            variant="icon"
            filePath={filePath}
            fileName={fileName}
            partnerDriverId={partnerDriverId}
            stepId={stepId}
            documentId={documentId}
            fileExtension={fileExtension}
          />
        </div>
      </div>
      <FileModal
        fullScreen={fullScreen}
        content={content}
        isOpen={isPreviewOpen}
        onClose={closePreview}
        filePath={filePath}
        fileName={fileName}
        partnerDriverId={partnerDriverId}
        stepId={stepId}
        documentId={documentId}
        fileExtension={fileExtension}
        header={header}
      />
    </div>
  )
}

export const FileWithPreview = memo(FileWithPreviewComponent)
